import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, TYPE_META } from "@/lib/api";
import { toast } from "sonner";
import { MoreHorizontal, Trash2, Heart, MessageCircle, Repeat2 } from "lucide-react";
import PostComments from "@/components/PostComments";
import ReshareModal from "@/components/ReshareModal";

/**
 * PostCard — one Commons post. Renders author byline, chronological date,
 * post body, optional attached-item card OR reshared-original block, a quiet
 * "…" menu for owners (delete), and a 3-up interaction row (Like / Comment /
 * Reshare) with visible counts. Comments collapse by default.
 */
export default function PostCard({ post: initial, isOwner = false, onDeleted, showAuthor = true, currentUserId }) {
  const [post, setPost] = useState(initial);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const menuRef = useRef(null);

  // Sync when parent swaps the post object (e.g. after optimistic feed reload).
  useEffect(() => { setPost(initial); }, [initial]);

  // Close the "…" menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  async function doDelete() {
    if (deleting) return;
    if (!window.confirm("delete this post? it can't be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/commons/posts/${post.id}`);
      toast.success("post deleted");
      onDeleted?.(post.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not delete");
    } finally {
      setDeleting(false);
      setMenuOpen(false);
    }
  }

  async function toggleLike() {
    if (likeBusy) return;
    setLikeBusy(true);
    // Optimistic: flip immediately, revert on error.
    const prevLiked = post.liked_by_me;
    const prevCount = post.like_count || 0;
    setPost(p => ({ ...p, liked_by_me: !prevLiked, like_count: prevCount + (prevLiked ? -1 : 1) }));
    try {
      const r = await api.post(`/commons/posts/${post.id}/like`);
      setPost(p => ({ ...p, liked_by_me: r.data.liked_by_me, like_count: r.data.like_count }));
    } catch (e) {
      setPost(p => ({ ...p, liked_by_me: prevLiked, like_count: prevCount }));
      toast.error(e?.response?.data?.detail || "could not toggle like");
    } finally {
      setLikeBusy(false);
    }
  }

  function onCommentCountChange(n) {
    setPost(p => ({ ...p, comment_count: n }));
  }

  function onReshared(_data) {
    setPost(p => ({
      ...p,
      reshare_count: (p.reshare_count || 0) + 1,
      reshared_by_me: true,
      my_reshare_id: _data?.id || p.my_reshare_id,
    }));
  }

  const snap = post.item_snapshot;
  const hasAttachment = Boolean(snap && (snap.title || snap.image_url));
  const typeLabel = snap ? (TYPE_META[snap.type]?.label?.toLowerCase() || snap.type || "") : "";
  const isReshare = Boolean(post.reshare_of);

  return (
    <article className="placed-card p-5 relative" data-testid={`post-card-${post.id}`}>
      {isOwner && (
        <div className="absolute top-3 right-3" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen(v => !v)}
            aria-label="post actions"
            data-testid={`post-menu-btn-${post.id}`}
            className="p-1 rounded-full text-[var(--cream-mute)] hover:text-[var(--brass)] hover:bg-[rgba(179,137,90,0.08)] transition-colors"
          >
            <MoreHorizontal size={16} strokeWidth={1.6} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 mt-1 w-40 bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.24)] rounded shadow-lg overflow-hidden z-10"
              data-testid={`post-menu-${post.id}`}
            >
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting}
                data-testid={`post-delete-${post.id}`}
                className="w-full text-left px-3 py-2 font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[#C86A5C] hover:bg-[rgba(200,106,92,0.08)] inline-flex items-center gap-2"
              >
                <Trash2 size={12} strokeWidth={1.6} />
                {deleting ? "deleting…" : "delete"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Reshare credit — rendered BEFORE the author byline when this post reshares another */}
      {isReshare && post.original && !post.original.deleted && (
        <p className="flex items-center gap-1.5 font-mono-lc text-[10px] tracking-[0.14em] uppercase text-[var(--cream-mute)] mb-3" data-testid={`post-reshare-credit-${post.id}`}>
          <Repeat2 size={11} strokeWidth={1.6} className="text-[var(--brass)]" />
          reshared from {post.original.author?.name || "an author"}
        </p>
      )}
      {isReshare && post.original?.deleted && (
        <p className="font-garamond italic text-xs text-[var(--cream-mute)] mb-3" data-testid={`post-reshare-tombstone-${post.id}`}>
          [the original post was removed]
        </p>
      )}

      {/* Author byline */}
      {showAuthor && post.author && (
        <div className="flex items-center gap-3">
          <Link
            to={post.author.username ? `/u/${post.author.username}` : "#"}
            data-testid={`post-author-${post.id}`}
            className="flex items-center gap-3"
          >
            {post.author.picture ? (
              <img src={post.author.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-[rgba(179,137,90,0.28)]" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[var(--bg-panel-hi)] border border-[rgba(179,137,90,0.28)] flex items-center justify-center font-serif-lit text-sm text-[var(--brass)]">
                {(post.author.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-serif-lit text-[var(--cream)] text-sm leading-tight truncate">{post.author.name || "friend"}</p>
              {post.author.username && (
                <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">@{post.author.username}</p>
              )}
            </div>
          </Link>
          <span className="ml-auto font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">
            {formatDate(post.created_at)}
          </span>
        </div>
      )}

      {!showAuthor && (
        <div className="flex items-baseline gap-2">
          {typeLabel && (
            <>
              <p className="font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--brass)]">{typeLabel}</p>
              <span className="font-mono-lc text-[10px] text-[var(--cream-mute)]">·</span>
            </>
          )}
          <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">{formatDate(post.created_at)}</p>
        </div>
      )}

      {/* Resharer's optional note (appears above the quoted original) */}
      {post.note && (
        <p
          className={`${showAuthor ? "mt-4" : "mt-2"} font-garamond italic text-[var(--cream)] text-[15px] sm:text-base leading-relaxed whitespace-pre-wrap break-words`}
          data-testid={`post-body-${post.id}`}
        >
          {post.note}
        </p>
      )}

      {/* Reshare: show the original post as a nested quote */}
      {isReshare && post.original && !post.original.deleted && (
        <div
          className="mt-4 bg-[var(--bg)] border border-[rgba(179,137,90,0.16)] rounded p-3"
          data-testid={`post-original-${post.id}`}
        >
          {post.original.note && (
            <p className="font-garamond italic text-sm text-[var(--cream-dim)] whitespace-pre-wrap break-words">
              {post.original.note}
            </p>
          )}
          {post.original.item_snapshot && (
            <ItemSnapshotRow snap={post.original.item_snapshot} compact />
          )}
        </div>
      )}

      {/* Attached library item on NON-reshare posts (reshares already show the
          original's snapshot inside the quote block). */}
      {!isReshare && hasAttachment && (
        <ItemSnapshotRow snap={snap} testId={`post-attachment-${post.id}`} />
      )}

      {/* Interaction row — Like / Comment / Reshare, with visible counts.
          Reshare is only available on PUBLIC posts (all Commons posts today
          are public; the private-collection gate lives elsewhere). */}
      <div className="mt-4 flex items-center gap-5 pt-3 border-t border-[rgba(179,137,90,0.12)]" data-testid={`post-interactions-${post.id}`}>
        <button
          type="button"
          onClick={toggleLike}
          disabled={likeBusy}
          aria-pressed={post.liked_by_me}
          data-testid={`post-like-${post.id}`}
          className={`inline-flex items-center gap-1.5 font-mono-lc text-[11px] tracking-[0.12em] transition-colors ${
            post.liked_by_me ? "text-[#D77D74]" : "text-[var(--cream-mute)] hover:text-[#D77D74]"
          }`}
        >
          <Heart size={14} strokeWidth={1.6} fill={post.liked_by_me ? "currentColor" : "none"} />
          <span data-testid={`post-like-count-${post.id}`}>{post.like_count || 0}</span>
        </button>

        <button
          type="button"
          onClick={() => setCommentsOpen(v => !v)}
          aria-expanded={commentsOpen}
          data-testid={`post-comment-btn-${post.id}`}
          className="inline-flex items-center gap-1.5 font-mono-lc text-[11px] tracking-[0.12em] text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
        >
          <MessageCircle size={14} strokeWidth={1.6} />
          <span data-testid={`post-comment-count-${post.id}`}>{post.comment_count || 0}</span>
        </button>

        {!isOwner && (
          <ReshareButton
            post={post}
            onReshared={onReshared}
            onUndone={() => setPost(p => ({
              ...p,
              reshared_by_me: false,
              my_reshare_id: null,
              reshare_count: Math.max(0, (p.reshare_count || 0) - 1),
            }))}
          />
        )}
        {isOwner && (
          <span
            data-testid={`post-reshare-count-${post.id}`}
            className="inline-flex items-center gap-1.5 font-mono-lc text-[11px] tracking-[0.12em] text-[var(--cream-mute)]"
            title="your own posts can't be reshared from here"
          >
            <Repeat2 size={14} strokeWidth={1.6} />
            {post.reshare_count || 0}
          </span>
        )}
      </div>

      {commentsOpen && (
        <PostComments
          postId={post.id}
          postOwnerId={post.user_id}
          currentUserId={currentUserId}
          onCountChange={onCommentCountChange}
        />
      )}
    </article>
  );
}

/**
 * ReshareButton — three-state affordance:
 *   • not reshared yet  → opens the ReshareModal to compose a note
 *   • reshared by me   → shows "Reshared" with a filled icon + click asks
 *                        "Undo reshare?" and DELETE's the reshare post
 *   • during a request → disabled, so repeated taps / double-fire are safe
 * All three states keep the same testid so test selectors stay stable.
 */
function ReshareButton({ post, onReshared, onUndone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function undo() {
    if (busy) return;
    if (!window.confirm("undo reshare? your reshared copy will be removed.")) return;
    setBusy(true);
    try {
      // my_reshare_id is populated by the backend list endpoint when the
      // viewer has already reshared this root post.
      await api.delete(`/commons/posts/${post.my_reshare_id}`);
      toast.success("reshare undone");
      onUndone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "could not undo reshare";
      toast.error(typeof msg === "string" ? msg : "could not undo reshare");
    } finally {
      setBusy(false);
    }
  }

  const already = post.reshared_by_me;
  const label = post.reshare_count || 0;

  return (
    <>
      <button
        type="button"
        onClick={already ? undo : () => setOpen(true)}
        disabled={busy}
        aria-pressed={already}
        data-testid={`post-reshare-btn-${post.id}`}
        title={already ? "undo reshare" : "reshare to your profile"}
        className={`inline-flex items-center gap-1.5 font-mono-lc text-[11px] tracking-[0.12em] transition-colors disabled:opacity-60 ${
          already ? "text-[var(--brass)]" : "text-[var(--cream-mute)] hover:text-[var(--brass)]"
        }`}
      >
        <Repeat2 size={14} strokeWidth={1.6} fill={already ? "currentColor" : "none"} />
        <span data-testid={`post-reshare-count-${post.id}`}>{label}</span>
        {already && <span className="font-mono-lc text-[9px] tracking-[0.18em] uppercase opacity-80">reshared</span>}
      </button>
      {open && (
        <ReshareModal
          post={post}
          onClose={() => setOpen(false)}
          onReshared={(data) => { setOpen(false); onReshared?.(data); }}
        />
      )}
    </>
  );
}

function ItemSnapshotRow({ snap, compact = false, testId }) {
  const typeLabel = TYPE_META[snap.type]?.label?.toLowerCase() || snap.type || "";
  return (
    <div
      className={`${compact ? "mt-2" : "mt-4"} flex gap-3 bg-[var(--bg)] border border-[rgba(179,137,90,0.16)] rounded p-3`}
      data-testid={testId}
    >
      {snap.image_url && (
        <img
          src={snap.image_url}
          alt=""
          className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-[2px] border border-[rgba(179,137,90,0.2)] shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-mono-lc text-[10px] tracking-[0.14em] uppercase text-[var(--brass)] opacity-80">
          {typeLabel}
        </p>
        <p className="font-serif-lit text-[var(--cream)] text-base leading-snug truncate">
          {snap.title || "untitled"}
        </p>
        {snap.creator && (
          <p className="font-garamond italic text-[var(--cream-dim)] text-sm truncate">by {snap.creator}</p>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }).toLowerCase();
  } catch { return ""; }
}
